<template>
  <div>
    <!-- Quote Edit Form -->
    <div id="quote-edit">
      <quote-header ref="headerDetails" @header-validation="isValidHeader = !($event)" />
      <section class="quotes--quote-summary">
        <b-card class="quotes-summary">
          <b-row>
            <b-col cols="2">
              <div class="tools first-row">
                <item-requirements
                  v-if="checkClaim(CAN_ADD_REQUIREMENTS)"
                  :companyId="quote.companyId"
                  pageName="quotes"
                  @updateLineItem="onRefresh"
                  @link-action="reqLinkAction"
                />
              </div>
            </b-col>
            <b-col>
              <div class="tools add-part" v-if="checkClaim(CAN_ADD_SALES_ORDER)">
                <span class="add-part">Select the items to send to the sales order, then:</span>
                <b-button
                  size="sm"
                  variant="primary"
                  @click="onCreateSalesOrder()"
                  :disabled="!checkedRowItemIds.length || !isAuthorized || isCreatingSO"
                  class="add-part"
                  >
                <i v-if="isCreatingSO" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>
                Create Sales Order</b-button>
              </div>
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <quote-lines
              ref="qLine-component"
              :quoteId="quoteId"
              :gridId="gridId"
              :key="componentKey"
              @selectedQuoteLine="setQuoteline"
              @showEdit="onShowEdit"
              @updateCheckedRowItemIds="checkedRowItemIds = $event"
            />
          </b-row>
        </b-card>
      </section>
      <b-tabs>
        <b-tab title="Summary" active>
          <b-row class="justify-content-between mt-2">
            <b-col cols="2">
              <div class="buttom-container">
                <div class="d-flex justify-content-between">
                  <a class="input-header-txt">Print Comments</a>
                </div>
                <lookup-dropdown
                  :options="printComments"
                  moduleName="printComments"
                  propKey="name"
                  resource="PrintComments"
                  lookupId="19"
                  class="mb-2"
                  @change="onPrintCommentChange"
                />
                <b-textarea rows="5" v-model.trim="quote.comments" />
              </div>
              <div class="buttom-container mt-2">
                <b-form-group label="Internal Comments">
                  <b-textarea v-model="quote.internalComments" />
                </b-form-group>
              </div>
            </b-col>
            <b-col cols="3">
              <calculator
                :lineObj="quoteLine"
                @reset="resetQuoteline"
                @updateLine="onUpdateQuoteLine"
                :key="componentKey"
                :exchangeRate="quote.exchangeRate"
              />
            </b-col>
            <b-col cols="5">
              <b-tabs>
                <b-tab title="Summary" active>
                  <quote-summary v-bind:quote="quote" />
                </b-tab>
                <b-tab>
                  <template v-slot:title>
                    Charges &nbsp;<b-badge variant="primary">{{ charges.length }}</b-badge>
                  </template>
                  <charges-list :quoteId="quoteId" @updateCharge="onRefresh" />
                </b-tab>
              </b-tabs>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab>
          <template v-slot:title>
            Sources &nbsp;
            <b-badge variant="primary" v-if="sourcesList.filter(x => x.isPreferred).length > 0">{{ sourcesList.length }}</b-badge>
            <i class="fa fa-warning" style="color:red" v-else></i>
          </template>
          <sources
            :quoteLineId="quoteLine.id"
            @onChangeSource="onRefresh"
            @close-offer-form="onCloseOffer"
          />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_ALL_OFFERS)">
          <template v-slot:title>
            Offers &nbsp;<b-badge variant="primary">{{ offersList.length }}</b-badge>
          </template>
          <offers
            :partId="quoteLine.partId"
            :quoteLineId="quoteLine.id"
            :quoteId="quoteId"
            :gridId="requirementOffersGridId"
            :isQuoteLine="true"
            @posted-source="onRefresh"
            @close-offer-form="onCloseOffer"
          />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_INVENTORY)">
          <template v-slot:title>
            I/C/E &nbsp;<b-badge variant="primary">{{ inventoryListForPart.length }}</b-badge>
          </template>
          <inventory-list
            :isQuoteLineView="true"
            :partId="quoteLine.partId"
            :quoteLineId="quoteLine.id"
            :gridId="inventoryQuotesGridId"
            hidePrint
          />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_ALL_POS)">
          <template v-slot:title>
            On Order &nbsp;<b-badge variant="primary">{{ purchaseOrderLines.length }}</b-badge>
          </template>
          <on-order
            :partId="quoteLine.partId"
            :crfqLineId="quoteLine.requirementLineId"
            :quoteLineId="quoteLine.id"
            :isQuoteLineView="true"
            isAvailableParts
            isPOTab
          />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_ALL_REQUIREMENTS)">
          <template v-slot:title>
            Requirements &nbsp;<b-badge variant="primary">{{ filteredRequirement.length }}</b-badge>
          </template>
          <requirements ref="reqt-component" :requirementLineId="quoteLine.requirementLineId" :quoteLineId="quoteLine.id" @link-action="reqLinkAction" @updateLineItem="onRefresh" showLinkBtn isQuoteLineView />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_PARTS)">
          <template v-slot:title>
            Cross/Alternatives  &nbsp;<b-badge variant="primary">{{ alternativesList.length }}</b-badge>
          </template>
          <alternative-list :partId="quoteLine.partId" :crfqLineId="quoteLine.id" :statusId="quoteLine.statusId"
            quoteReplace :gridId="getGridId('alternative')" @updatedQuote="onRefresh" />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_ALL_QUOTE)">
          <template v-slot:title>
            Quote History  &nbsp;<b-badge variant="primary">{{ quotesFiltered.length }}</b-badge>
          </template>
          <quotes :partId="quoteLine.partId" isQuoteTab />
        </b-tab>
        <b-tab v-if="checkClaim(CAN_VIEW_ALL_INVOICES)">
          <template v-slot:title>
            Invoices  &nbsp;<b-badge variant="primary">{{ invoicesFiltered.length }}</b-badge>
          </template>
          <invoices
            :partId="quoteLine.partId"
            :quoteLineId="quoteLine.id"
            isInvoiceTab
          />
        </b-tab>
        <!-- <b-tab title="RMAs" v-if="checkClaim(CAN_VIEW_ALL_RMAS)"></b-tab> -->
        <b-tab title="Documents and Images">
          <documents-and-images
            :reference="'quoteLineId'"
            :refId="quoteLine.id"
            :partId="quoteLine.partId"
          />
        </b-tab>
      </b-tabs>
      <div class="d-flex justify-content-between mt-2">
        <div></div>
        <div class="mr-3">
          <b-button
            type="button"
            variant="secondary"
            size="sm"
            @click="onCancel"
            v-if="checkClaim(CAN_VIEW_ALL_QUOTE)"
            >Exit</b-button
          >
          <b-button
            variant="primary"
            type="submit"
            class="ml-2"
            size="sm"
            @click="onClosed"
            :disabled="isSaving"
            v-if="checkClaim(CAN_EDIT_QUOTES) && quote.statusId != 205"
          >
            <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i
            > {{ quote.statusId != 456 ? 'Close Quote' : 'Reopen Quote' }}
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            class="ml-2"
            size="sm"
            @click="onSave(false)"
            :disabled="isSaving"
            v-if="checkClaim(CAN_EDIT_QUOTES)"
          >
            <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i
            >Save and Continue
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            class=" ml-2"
            size="sm"
            @click="onSave(true)"
            :disabled="isSaving"
            v-if="checkClaim(CAN_EDIT_QUOTES) && checkClaim(CAN_VIEW_ALL_QUOTE)"
          >
            <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i
            >Save and Exit</b-button
          >
          <b-button
            :variant="isAuthorized ? 'success' : 'danger'"
            size="sm"
            class="ml-2"
            :disabled="isSaving"
            @click="onAuthorize"
          >
            <i v-if="isSaving" class="fa fa-spinner fa-pulse fa-fw mr-1"></i
            >{{ isAuthorized ? 'Unauthorize' : 'Authorize' }}
          </b-button>

          <b-button
            variant="primary"
            size="sm"
            @click="onEmailQuote"
            class="ml-2"
            :disabled="!isAuthorized"
          >
            <i v-show="isEmailLoading" class="fa fa-spinner fa-pulse fa-fw"></i>
            Send Email
          </b-button>
          <send-emails
            :sendEmailData="sendEmailData"
            @refresh="onRefresh"
          ></send-emails>
          <!-- <b-button variant="primary" size="sm" @click="onPrintQuote" class="ml-2">
            <i v-show="isPrintLoading" class="fa fa-spinner fa-pulse fa-fw"></i> Print
          </b-button> -->
          <!-- <b-dropdown variant="primary" size="sm" text="Print" class="ml-2" :disabled="!isAuthorized">
            <b-dropdown-item v-for="(option, index) in reportSelectionOptions" :key="index" @click="$bvModal.show(option.reportName+'-report-modal')"> {{ option.text }} </b-dropdown-item>
          </b-dropdown> -->
          <!-- <b-button
            :disabled="!isAuthorized"
            @click="$bvModal.show('view-fast-report')"
            class="ml-2"
            variant="primary"
            size="sm"
          >
            Print
          </b-button> -->
        </div>
      </div>
    </div>

    <!-- Part Modal -->
    <div id="add-part-popup" class="d-inline pull-left">
      <b-modal
        id="add-part-modal"
        ref="add-part-modal"
        size="xl"
      >
        <template slot="modal-header">
          <h5 class="modal-title">Edit Quote Detail</h5>
        </template>

        <line-details
          ref="editLineDetails"
          :lineObj="quoteLine"
          @partChange="onPartChange"
          @lineChange="onLineChange"
          @headerChange="onHeaderChange"
          @line-validation="isLineInvalid = $event"
          isRequiredWarrantyLeadTime 
        />
        <template slot="modal-footer">
          <b-button @click="handleCancel">Cancel</b-button>
          <b-button v-if="checkClaim(CAN_EDIT_QUOTES)" class="mr-2" variant="primary" @click="handleOk" :disabled="isSavingLine">
            <i v-if="isSavingLine" class="fa fa-spinner fa-pulse fa-fw mr-1"></i>Save</b-button>          
        </template>
      </b-modal>
    </div>

    <!-- <div v-for="(option, index) in reportSelectionOptions" :key="index">
      <b-modal :id="option.reportName + '-report-modal'" :ref="option.reportName + '-report-modal'" hide-footer>
        <report-viewer :filePath="'ReportsRoot/'+option.fileName+'.flxr'" :parameter="{Quote: quoteId}" :reportName="option.reportName" />
      </b-modal>
    </div> -->
    <!-- <report-viewer
      :parameter="{ Quote: quoteId }"
      :reportsList="reportSelectionOptions"
    ></report-viewer> -->

    <!-- <fast-report-viewer :parameter="quoteId" :moduleName="'Quotes'" /> -->
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
// import debounce from 'lodash/debounce';
import { GRID_ID, REPORTS_CONSTANTS } from '@/shared/constants';

import reportApi from '@/resources/reportApi';

import QuoteLines from './../QuoteLines';
import QuoteHeader from './components/QuoteHeader';
import QuoteSummary from './components/QuoteSummary';
import Charges from './components/Charges';
import PartForm from '@/shared/components/Parts';
import QuoteLineEdit from '@/shared/components/QuoteLine';
import RequirementDetails from '@/shared/components/RequirementDetails/index';
import LineDetails from '@/shared/components/LineDetails/index';
//import Sources from '@/shared/components/Sources';
import Offers from '@/shared/components/Offers';
import AlternativeList from '@/shared/components/Alternatives';
import Requirements from '@/shared/components/Requirements';
import SendEmails from '@/shared/components/SendEmails/index';
import ItemRequirements from '@/shared/components/ItemRequirements/index';
import ChargesList from '@/shared/components/ChargesList/index';
import Calculator from '@/shared/components/Calculator';
import Sources from '@/shared/components/QuoteLineSources';
//import PurchaseOrders from '@/shared/components/PurchaseOrders';
import OnOrder from '@/shared/components/OnOrder';
import InventoryList from '@/shared/components/InventoryList';
import Invoices from '@/shared/components/InvoiceLines';
import Quotes from '@/shared/components/Quotes';
import ReportViewer from '@/shared/components/ReportViewer';
import DocumentsAndImages from '@/shared/components/DocumentsAndImages';
import FastReportViewer from '@/shared/components/FastReportViewer';
//import Requirements from './components/Tabs/Requirements';

export default {
  data() {
    return {
      gridId: GRID_ID.QUOTE_EDIT_QUOTE_LINES,
      inventoryQuotesGridId: GRID_ID.INVENTORY_QUOTES_GRID,
      partIds: [],
      componentKey: 0,
      partId: 0,
      selectedCompany: 0,
      company: {},
      fullAddressString: '',
      address: '',
      quoteLine: {},
      cloneQuoteLine: {},
      //marginPercent: '',
      // isAuthorized: false,
      isPrintLoading: false,
      isEmailLoading: false,
      checkedQuoteReports: [],
      checkedRowItemIds: [],
      mergedObj: {},
      requirementOffersGridId: GRID_ID.REQUIREMENT_OFFERS,
      printFilePath: null,
      sendEmailData: {},
      selectedPrintReport: null,
      isSaving: false,
      reportSelectionOptions: [
        {
          text: 'Quote',
          reportName: 'Quote',
          fileName: 'Quote',
          defaultSelected: true,
        },
        {
          text: 'Terms and Condition',
          reportName: 'Quote - TermsAndCond',
          fileName: 'TermsAndCond',
        },
        {
          text: 'CC Authorization',
          reportName: 'Quote - CreditCardAuth',
          fileName: 'CreditCardAuth',
        },
        { text: 'NCNR', reportName: 'Quote - NCNR', fileName: 'NCNR' },
      ],
      emailReports: [],
      quoteLineOpenStatus: 206,
      isCreatingSO: false,
      isLineInvalid: false,
      isSavingLine: false,
      isValidHeader: false,
      linkFilterObj: null
    };
  },
  components: {
    QuoteLines,
    QuoteHeader,
    QuoteSummary,
    Charges,
    ChargesList,
    Requirements,
    ItemRequirements,
    PartForm,
    QuoteLineEdit,
    RequirementDetails,
    LineDetails,
    Calculator,
    Sources,
    InventoryList,
    //PurchaseOrders,
    OnOrder,
    Invoices,
    Offers,
    AlternativeList,
    Quotes,
    ReportViewer,
    SendEmails,
    DocumentsAndImages,
    FastReportViewer
  },
  metaInfo() {
    return {
      title: `Quote: ${this.quoteId} - ${this.quote.companyName}`,
    };
  },
  computed: {
    ...mapGetters('requirements', ['filteredRequirement' ]),
    ...mapGetters('quoteLineSources', ['sourcesList' ]),
    ...mapGetters('quoteLines', ['quoteLinesById']),    
    ...mapGetters('alternatives', ['alternativesList' ]),
    ...mapGetters('offers', ['offersList']),
    ...mapGetters('lookup', ['printComments']),
    ...mapGetters('quoteDetails', ['quoteById']),
    ...mapGetters('quotes', ['quote', 'quotesFiltered', 'charges']),
    ...mapGetters('parts', ['documentsLists']),
    ...mapGetters('purchaseorders', [ 'purchaseOrderLines' ]),
    ...mapGetters('inventory', [ 'inventoryListForPartById' ]),
    ...mapGetters('invoices', [ 'invoicesFiltered' ]),
    //...mapGetters('companies', ['company']),
    inventoryListForPart() {
      return this.inventoryListForPartById(this.quoteLine.partId);
    },
    quoteLines() {
      return this.quoteLinesById(this.quoteId);
    },
    quoteId() {
      return parseInt(this.$route.params.id);
    },
    companyId() {
      return this.$route.params.companyId;
    },
    quote() {
      return this.quoteById(this.quoteId);
    },
    isAuthorized() {
      return this.quote.dateAuthorized && this.quote.authorizedByUserFullName;
    },
    reportOptions() {
      return REPORTS_CONSTANTS.filter(x => x.module == 'Quotes');
    }
  },
  methods: {
    ...mapActions('quotes', ['update']),
    ...mapActions('salesOrders', ['createSalesOrder']),
    // ...mapActions('companies', { companyById: 'getById' }),
    ...mapActions('quoteLines', [
      'getByQuoteId',
      // 'deleteQuoteLine',
      'updateQuoteLine',
      'createQuoteLine',
    ]),

    ...mapActions('quoteDetails', [
      'getById',
      'getContacts',
      'authorizeQuote',
      'unAuthorizeQuote',
      'closeQuote',
      'updateQuote'
    ]),

    ...mapActions('quoteAdd', [
      'getContacts',
      'createQuote',
    ]),

    // ...mapMutations('quoteDetails', ['setQuote']),
    // ...mapMutations('quoteLines', ['setQuoteLines']),
    ...mapMutations('quotes', ['setCharges']),
    async handleOk(bvModalEvt) {
      await this.$refs.editLineDetails.getLineValidation();
      if (!this.isLineInvalid) {
        try {
          this.isSavingLine = true;
          const result = await this.updateQuoteLine({
            ...this.quoteLine,
            ...this.mergedObj,
            id: this.quoteLine.id,
          });
          this.isSavingLine = false;
        } catch {
        } finally {
          this.onRefresh();
          
          this.$bvModal.hide('add-part-modal');
          this.isSavingLine = false;
        }
      }      
    },

    async printReport(quoteId, reports) {
      console.log(reports, 'Test');
      const { data } = await reportApi.get(
        'api/report/quoteReport/ ' + quoteId,
        {
          name: 'document',
          responseType: 'blob',
        }
      );

      const fileUrl = URL.createObjectURL(data);
      var a = document.createElement('a');
      document.body.appendChild(a);
      a.style = 'display: none';
      a.href = fileUrl;
      a.download = 'Quote.pdf';
      a.click();
      window.URL.revokeObjectURL(fileUrl);
      document.body.removeChild(a);
    },
    async onPrintQuote() {
      this.$refs['reportSelectionModal'].show();
    },
    async onPreviewPrintQuote() {
      try {
        this.isPrintLoading = true;
        this.printFilePath = 'QuoteReport2/' + this.quoteId;
        this.$bvModal.show('view-report-modal');

        this.isPrintLoading = false;
      }catch (e) {
        this.isPrintLoading = false;
      }
    },
    async onEmailQuote($event) {
      this.$bvModal.hide('view-fast-report');
      // this.emailReports = $event
      this.sendEmailData = {
        template: 'Quote',
        source: 'Quote',
        to: this.quote.contactEmail,
        ReferenceId: this.quoteId,
        // isMultipleAttachment: true,
        reportSelectionOptions: this.reportOptions,
        documentsSelection: this.documentsLists,
      };
      this.$root.$emit('bv::show::modal', 'send-email-modal');
    },
    resetQuoteline() {
      this.quoteLine = { ...this.cloneQuoteLine };
    },
    handleCancel() {
      this.resetQuoteline();
      this.$bvModal.hide('add-part-modal');
    },

    updateTotal(emit) {
      console.log('updating totals');
      console.log(emit);
      if (emit) {
        this.quote.total = Number(emit.total).toLocaleString('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
        });
        this.quote.subTotal = Number(emit.subTotal).toLocaleString();
        this.quote.gpMargin = emit.gpMargin;
        this.quote.grossProfit = Number(emit.grossProfit).toLocaleString();
        this.quote.totalCharges = Number(emit.charges).toLocaleString();
      }
    },
    async initializeModal() {},
    setQuoteline(quoteLine) {
      this.quoteLine = JSON.parse(JSON.stringify(quoteLine));

      this.quoteLine = {
        ...this.quoteLine,
        currencyTypeId: this.quote.currencyTypeId,
        exchangeRate: this.quote.exchangeRate,
      };

      this.cloneQuoteLine = quoteLine;
      //this.marginPercent = this.profit / this.totalPrice * 100;
    },
    async onShowEdit(quoteLine) {
      console.log('onShowEdit');

      this.setQuoteline(quoteLine);
      this.$bvModal.show('add-part-modal');
    },
    async addItemClick() {
      console.log('addItemClick');
      this.onSave(false);
    },
    onAddPart() {
      this.$bvModal.show('add-part-modal');
    },
    onCancel() {
      this.$router.push({ name: 'quotes' });
    },
    async onRefresh() {
      await this.onSave(false);
      this.getById(this.quoteId);
      this.getByQuoteId(this.quoteId).then(() => {
        // update object
        if (Object.keys(this.quoteLine).length > 0) {
          const selected = this.quoteLines.find(line => line.id === this.quoteLine.id);
          this.setQuoteline(selected);
        }
      });
      // this.componentKey += 1;
    },
    async chgPartId(part) {
      //alert(part.id);
      eventBus.$emit('chgPartId', this.part);
      this.partId = part.id;
    },
    async addPart(part) {
      if (part.Id == 0) return;
      this.quoteLine = {
        id: 0,
        quoteId: this.quoteId,
        partId: part.id,
        buyerPrice: part.buyerPrice,
        price: part.price,
        quantity: part.quantity,
      };
      try {
        await this.createQuoteLine(this.quoteLine);
      } catch {
      } finally {
        this.onRefresh();
      }
    },
    async onSave(exit, isAuthorize = false) {
      try {
        
        await this.$refs.headerDetails.getHeaderValidation();
        if (this.isValidHeader) {
          this.isSaving = true;
          await this.updateQuote({quote: this.quote, isAuthorize});
          this.isSaving = false;
          if (exit) {
            this.$router.push({name: 'quotes'});
          }
        }
      }catch (e) {
        this.isSaving = false;
      }
    },
    openImageSliderModal() {
      this.$refs['image-slider-modal'].show();
    },
    async onClosed() {
      try {
        this.isSaving = true;
        await this.closeQuote({quoteId: this.quoteId, isClose: this.quote.statusId != 456});

        this.isSaving = false;
        await this.getById(this.quoteId);
        this.getByQuoteId(this.quoteId).then(() => {
          // update object
          if (Object.keys(this.quoteLine).length > 0) {
            const selected = this.quoteLines.find(line => line.id === this.quoteLine.id);
            this.setQuoteline(selected);
          }
        });
      }catch (e) {
        this.isSaving = false;
      }
    },
    async onAuthorize() {
      if (this.isAuthorized) {
        await this.onSave(false, !this.isAuthorized);
      } else {
        await this.$refs.headerDetails.getHeaderValidation();
        if (this.isValidHeader) {
          await this.onSave(false, !this.isAuthorized);
        }
      }
    },
    async onUpdateQuoteLine(quoteLineObj) {

      await this.updateQuoteLine(quoteLineObj);

      await this.onRefresh();
    },
    async onCreateSalesOrder() {
      this.isCreatingSO = true;
      await this.onSave(false, true);
      // this.authorizeQuote(this.quoteId);
      const salesOrder = await this.createSalesOrder({
        quoteId: this.quoteId,
        ids: this.checkedRowItemIds,
      }).catch(() => this.isCreatingSO = false);
      this.isCreatingSO = false;
      if (salesOrder) {
        this.$router.push({
          name: 'salesorders.edit',
          params: { id: salesOrder.id },
        });
      }
    },
    onPartChange(partObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...partObj,
      };
    },
    onLineChange(lineObj) {
      this.mergedObj = {
        ...this.mergedObj,
        ...lineObj,
      };
    },
    onHeaderChange(headerObj) {
      console.log(headerObj, 'Header ');
      this.mergedObj = {
        ...this.mergedObj,
        ...headerObj,
      };
    },
    onPrintCommentChange({ text, name }) {
      this.quote.comments = this.quote.comments
        ? this.quote.comments + '\n' + (text || name)
        : text || name;
    },
    getGridId(tabName){
      switch (tabName) {
        case 'alternative':
          return GRID_ID.REQUIREMENT_ALTERNATIVES
          break
      }
    },
    onCloseOffer(event) {
      if (event) { this.onRefresh() }
    },
    async reqLinkAction(lineObj = null) {
      let obj = lineObj || this.quoteLine;
      if (lineObj != null) {
        this.$refs['qLine-component'].onRowClick({ dataItem: lineObj });
      }

      // UNlink the Requirement
      if (obj.requirementLineId > 0) {        
        const resp = await this.openConfirmBox({
          message: `Are you sure you want to unlink the requirement of this quote line #${obj.lineNum} ${obj.partNumber}`,
          okTitle: 'Yes, unlink to a requirement',
          cancelTitle: 'Cancel'
        })

        if (resp) {
          try {
            this.isSavingLine = true;
            const result = await this.updateQuoteLine({
              ...this.quoteLine,
              requirementLineId: 0
            });
            this.isSavingLine = false;
            this.quoteLine.requirementLineId = 0;
            // this.$refs['reqt-component'].getDataFilter();
          } catch {
          } finally {
            this.onRefresh();
            
            this.$bvModal.hide('add-part-modal');
            this.isSavingLine = false;
          }
        }

        
      } else { // Link to requirement
        let filterObj = {
          isOpen: true,
          partId: obj.partId,
          companyId: this.quote.accountId,
          salesPersonUserId: this.quote.salesPersonId,
        }
        this.$refs['reqt-component'].getToLinkRequirement(filterObj);
      }
    }
  },
  watch: {
    'quote.total': function(value) {
      // console.log(this.quote.total);
      // if (this.quote.total != value)
      // {
      //   this.total =  Number(this.quote.total).toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: 2 });
      //   console.log(this.quote.total);
      // }
      //this.quote.total = Number(this.quote.total).toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: 2 });
    },
  },
  async mounted() {
    this.setContainerLoader(true);
    await this.getById(this.quoteId).then(async () => {
      this.setContainerLoader(false);
      if (this.quote.companyId) {
        this.getContacts(this.quote.companyId);
        // this.company = await this.companyById(this.quote.companyId);
      }
    }).catch(() => {
      this.setContainerLoader(false);
      //return back to listing page because error in fetch record
      this.$router.push({name: 'quotes'});
    });
  },
};
</script>

<style lang="scss">
.quotes--quote-summary {
  .first-row {
    margin-bottom: 15px;
  }

  .add-part {
    margin-right: 15px;
  }
}

.buttom-container {
  margin-left: 5px;
}
</style>
